import React, { useEffect, useState } from 'react';
import { ProductCard } from '@components';
import { Typography, ContentCarousel, Button, Loader } from 'gd-design-library';

import styles from './ScrollableProductCards.module.scss';
import classnames from 'classnames/bind';
import { ProductsResponse, Product } from '@services';
import { LineItemDraft } from '@commercetools/platform-sdk';
import { ProductsRecommendations } from '@services/product/interfaces';
import { AnalyticsListContext, pushViewItemListEvent } from '@utils';

const cx = classnames.bind(styles);

const CN = 'scrollable-product-cards';
const headerClassName = `${CN}__header`;
const titleClassName = `${CN}__title`;
const logoClassName = `${CN}__logo`;
const carouselClassName = `${CN}__carousel`;
const carouselCardClassName = `${CN}__carousel-card`;
const loadMoreButtonClassName = `${CN}__load-more-button`;

interface ScrollableProductCardsProps {
  products: ProductsResponse | ProductsRecommendations | null;
  title: string;
  onAddToCart?: (lineItemDraft: LineItemDraft, currency?: string) => Promise<void>;
  logo?: string;
  analyticsContext: AnalyticsListContext;
  actionButton?: React.ReactNode;
  allowQuickCart?: boolean;
  displayLoadMore?: boolean;
  displayMobileAddButton?: boolean;
  onLoadMore?: () => Promise<void>;
}

export const ScrollableProductCards: React.FC<ScrollableProductCardsProps> = ({
  products,
  title,
  onAddToCart,
  onLoadMore,
  logo,
  analyticsContext,
  allowQuickCart = false,
  displayMobileAddButton = false,
  displayLoadMore,
  actionButton
}) => {
  const [ isLoading, setIsLoading ] = useState(false);

  useEffect(() => {
    pushViewItemListEvent(products?.results, analyticsContext);
  }, [ products?.results, analyticsContext?.listId, analyticsContext?.listName ]);

  let items = products?.results?.map(product => ({
    id: product.id,
    content: product
  })) || [];

  if (displayLoadMore) {
    items = [ ...items, { id: 'load-more', content: null } ];
  }

  const renderItem = (item: { id: string; content: Product | null }): React.ReactElement => {
    if (item.id === 'load-more') {
      return (
        <div key='load-more' className={ cx(loadMoreButtonClassName) }>
          {isLoading ? (
            <Loader size='md' name='circle' />
          ) : (
            <Button
              onClick={ async (): Promise<void> => {
                if (onLoadMore) {
                  setIsLoading(true);
                  await onLoadMore();
                  setIsLoading(false);
                }
              } }
              variant='outlined'
            >
              Load More
            </Button>
          )}
        </div>
      );
    }

    return (
      <ProductCard
        allowQuickCart={ allowQuickCart }
        handleAddToCart={ onAddToCart ?? (async (): Promise<void> => { throw new Error(); }) }
        key={ item.id }
        product={ item.content as Product }
        displayMobileAddButton={ displayMobileAddButton }
        analyticsContext={ analyticsContext }
        className={ cx(carouselCardClassName) }
      />
    );
  };

  return (
    <div className={ cx(CN) }>
      <div className={ cx(headerClassName) }>
        <Typography variant='h6' className={ cx(titleClassName) }>
          {title} {logo && <img
            className={ cx(logoClassName) }
            src={ logo }
            alt='Logo'
          />}
        </Typography>
        {actionButton}
      </div>
      <div className={ cx(carouselClassName) }>
        <ContentCarousel
          items={ items }
          renderItem={ renderItem }
          showArrows={ true }
          showDots={ false }
          isFocusable={ true }
        />
      </div>
    </div>
  );
};
